/**
 * The account, from the browser's side.
 *
 * Everything here talks to the SpacetimeDB module over its own HTTP API, with
 * the ID token from `SignIn` in an `Authorization` header. There is no SDK and
 * no generated bindings, and that is a decision rather than an omission: what
 * this client does with the module is create an account, read one back, and
 * list some rooms — four calls, no live subscriptions, nothing that would repay
 * a code generation step in the build and a second copy of the schema in the
 * repository. The day the lobby wants rooms appearing as they are opened,
 * without a refresh, is the day to reach for `spacetime generate`.
 *
 * Two kinds of call, and the difference is the whole security model:
 *
 *   **Reads** are anonymous. The tables are public, and a room directory is no
 *   more private than the game's own front page.
 *
 *   **Writes** are reducer calls carrying the token, and the module checks it
 *   itself — issuer, audience, signature — in `caller`. Nothing this file does
 *   is trusted by anything. If this code lied about who it was, the module
 *   would refuse it, and so would the hearth.
 */
import type { AuthSetup, SignIn } from './auth.ts';

/** An account as the module holds it. */
export interface Account {
  handle: string;
  created: number;
}

/** A room in the directory — the thing you can see before you walk in. */
export interface DirectoryRoom {
  code: string;
  name: string;
  seats: number;
  taken: number;
  open: boolean;
  played: number;
}

export class AccountService {
  constructor(
    private readonly setup: AuthSetup,
    private readonly signIn: SignIn,
  ) {}

  get available(): boolean {
    return !!(this.setup.stdbUri && this.setup.stdbDb);
  }

  /**
   * This person's account, or null if they have signed in and never taken one.
   *
   * That second state is a real one and the sign-up screen exists for it: an
   * identity provider will happily say who somebody is long before this game has
   * ever heard of them.
   */
  async mine(): Promise<Account | null> {
    const who = this.signIn.who;
    if (!who?.subject) return null;
    const rows = await this.sql(
      `SELECT handle, created FROM account WHERE subject = '${sqlSafe(who.subject)}'`,
    );
    const row = rows[0];
    if (!row) return null;
    return { handle: String(row[0]), created: Number(row[1] ?? 0) };
  }

  /** Is this name still free? Asked while somebody types, so it is only a read. */
  async handleFree(handle: string): Promise<boolean> {
    const key = handle.trim().toLowerCase();
    if (!key) return false;
    const rows = await this.sql(`SELECT handle FROM account WHERE handle_key = '${sqlSafe(key)}'`);
    return rows.length === 0;
  }

  /** Take an account. The one door a new player comes through. */
  async create(handle: string): Promise<void> {
    await this.call('create_account', [handle]);
  }

  /** Change what you are called. */
  async rename(handle: string): Promise<void> {
    await this.call('set_handle', [handle]);
  }

  /** Say you are still here, so "last seen" means something. */
  async touch(): Promise<void> {
    await this.call('touch', []);
  }

  /** Open a room, or reopen one you already own. */
  async openRoom(code: string, name: string, seats: number): Promise<void> {
    await this.call('open_room', [code, name, seats]);
  }

  /** Take a crown in a room, or change which crown you hold there. */
  async takeSeat(code: string, faction: string, race: string, kingdom: string): Promise<void> {
    await this.call('take_seat', [code, faction, race, kingdom]);
  }

  /** Give the seat up. The room plays on without you. */
  async leaveSeat(code: string): Promise<void> {
    await this.call('leave_seat', [code]);
  }

  /**
   * Every room anybody has opened, newest play first.
   *
   * Two reads rather than a join: the seat counts come back as their own rows
   * and are folded in here. A join would be one round trip instead of two and
   * would put a query shape in the client that the module would then have to
   * keep working — for a list that is a handful of rows on any hearth a person
   * would actually play on.
   */
  async rooms(): Promise<DirectoryRoom[]> {
    const [rooms, seats] = await Promise.all([
      this.sql('SELECT code, name, seats, open, played FROM room'),
      this.sql('SELECT code FROM seat'),
    ]);
    const taken = new Map<string, number>();
    for (const s of seats) {
      const code = String(s[0]);
      taken.set(code, (taken.get(code) ?? 0) + 1);
    }
    return rooms
      .map((r) => ({
        code: String(r[0]),
        name: String(r[1]),
        seats: Number(r[2] ?? 0),
        open: Boolean(r[3]),
        played: Number(r[4] ?? 0),
        taken: taken.get(String(r[0])) ?? 0,
      }))
      .sort((a, b) => b.played - a.played);
  }

  // ── the two shapes of request ──────────────────────────────────────────────

  /**
   * A reducer call, carrying the token.
   *
   * A refused reducer answers with the sentence its `Err` carried, which is
   * written for a player rather than a log — "somebody already goes by that
   * name" — so it is passed through rather than translated. That is why the
   * module's errors read the way they do.
   */
  private async call(reducer: string, args: unknown[]): Promise<void> {
    const token = await this.signIn.token();
    if (!token) throw new Error('sign in first');
    const res = await fetch(`${this.base}/call/${reducer}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(args),
    });
    if (!res.ok) {
      const said = (await res.text()).trim();
      throw new Error(said || `the accounts module answered ${res.status}`);
    }
  }

  /** A read. Public tables, no token, no standing required. */
  private async sql(query: string): Promise<unknown[][]> {
    const res = await fetch(`${this.base}/sql`, {
      method: 'POST',
      headers: { 'Content-Type': 'text/plain' },
      body: query,
    });
    if (!res.ok) throw new Error('the accounts module could not be reached');
    const body = (await res.json()) as { rows?: unknown[][] }[];
    return body[0]?.rows ?? [];
  }

  private get base(): string {
    return `${this.setup.stdbUri}/v1/database/${encodeURIComponent(this.setup.stdbDb ?? '')}`;
  }
}

/**
 * A value on its way into a query.
 *
 * Subjects come out of signed tokens and handles come off a keyboard, and both
 * are about to be pasted into SQL. Quotes are doubled, the SQL way; anything
 * with no business in either — a newline, a control character — is dropped
 * rather than escaped, because no legitimate name contains one.
 */
function sqlSafe(value: string): string {
  let out = '';
  for (const ch of value) {
    const code = ch.codePointAt(0) ?? 0;
    if (code < 0x20 || code === 0x7f) continue;
    out += ch === "'" ? "''" : ch;
  }
  return out;
}
